import { componentCls } from "@faire/slate/lib/tracking";
import { SkeletonBase } from "@faire/slate/private/loading-skeleton/SkeletonBase";
import {
  SkeletonVariant,
  ISkeletonWebProps,
  ISkeletonPropsConstraints,
} from "@faire/slate/private/loading-skeleton/type";
import { getSkeletonVars } from "@faire/slate/private/loading-skeleton/utils";
import React from "react";

type IProps = {
  /**
   * The diameter of the avatar.
   *
   * @default 64 (px)
   */
  diameter?: number | string;

  /**
   * The height of the skeleton.
   */
  height?: number | string;

  /**
   * The size of the image.
   *
   * @default 240 (px)
   */
  size?: number | string;

  /**
   * The variant of the skeleton. Each variant defines different props to customize the skeleton.
   * - `canvas`: custom width and height, default is 100%
   * - `square`: size of the square, default is 240px
   * - `circle`: diameter of the circle, default is 64px
   * - `titleText`: width of the title text, default is 136px, height is fixed 32px
   * - `paragraphText`: width of the paragraph text, default is 240px, height is fixed 20px
   * - `round`: width and height of a fully rounded rectangle, default is 136px by 40px
   *
   * @default canvas
   */
  variant?: SkeletonVariant;

  /**
   * The width of the skeleton.
   */
  width?: number | string;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ISkeletonWebProps;

/**
 * A loading skeleton represents the expected UI that will be displayed after an unspecified loading time.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Loading-Skeleton-2242efb5c25a8076ad7cc60c63a23003 | Slate/LoadingSkeleton}
 */
export const LoadingSkeleton: React.FC<ISkeletonPropsConstraints<IProps>> = ({
  diameter,
  height,
  size,
  variant = "canvas",
  width,
  ...props
}) => {
  const skeletonVars = getSkeletonVars(variant, {
    width,
    height,
    diameter,
    size,
  });

  return (
    <SkeletonBase
      className={componentCls()}
      data-component-name="Slate/LoadingSkeleton"
      style={skeletonVars}
      {...props}
      role="status"
    />
  );
};
